import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Stack,
  useTheme,
  alpha,
  Chip,
} from "@mui/material";

const AttendanceQuickView = ({
  attendanceRecords = [],
  totalEmployees = 0,
  loading = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const todayStr = useMemo(() => new Date().toISOString().split("T")[0], []);

  // Filter today's attendance records
  const todaysAttendance = useMemo(() => {
    return attendanceRecords.filter((r) => {
      const recDate = (r.date || r.check_in || r.created_at || "").split("T")[0];
      return !recDate || recDate === todayStr;
    }).sort((a, b) => {
      const timeA = new Date(b.check_in || b.check_out || b.created_at || 0).getTime();
      const timeB = new Date(a.check_in || a.check_out || a.created_at || 0).getTime();
      return timeA - timeB;
    });
  }, [attendanceRecords, todayStr]);

  // Stats Calculations
  const inOfficeCount = todaysAttendance.filter((r) => r.check_in && !r.check_out).length;
  const presentTodayCount = todaysAttendance.filter((r) => r.check_in).length;

  const totalStaff = totalEmployees || (todaysAttendance.length > 0 ? todaysAttendance.length : 1);

  const presentPercentageToday = useMemo(() => {
    if (totalStaff === 0) return 0;
    return Math.min(100, Math.round((presentTodayCount / totalStaff) * 100));
  }, [presentTodayCount, totalStaff]);

  // 10-Day Overall Attendance Percentage Calculation
  const tenDayAttendanceAvg = useMemo(() => {
    if (!attendanceRecords || attendanceRecords.length === 0) return presentPercentageToday;

    // Group check-ins by date
    const dateGroups = {};
    attendanceRecords.forEach((r) => {
      if (r.check_in) {
        const dateKey = (r.date || r.check_in || r.created_at || "").split("T")[0];
        if (dateKey) {
          if (!dateGroups[dateKey]) dateGroups[dateKey] = new Set();
          dateGroups[dateKey].add(r.user_id || r.employee_id || r.id || r.email || r.first_name);
        }
      }
    });

    const dates = Object.keys(dateGroups);
    if (dates.length === 0) return presentPercentageToday;

    const totalPercentages = dates.reduce((sum, dKey) => {
      const presentOnDate = dateGroups[dKey].size;
      const rateOnDate = Math.min(100, (presentOnDate / totalStaff) * 100);
      return sum + rateOnDate;
    }, 0);

    return Math.round(totalPercentages / dates.length);
  }, [attendanceRecords, totalStaff, presentPercentageToday]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 1,
        background: isDark ? "#111827" : "#ffffff",
        border: 1, borderColor: "divider",
        boxShadow: isDark
          ? "0 4px 20px rgba(0, 0, 0, 0.25)"
          : "0 4px 20px rgba(15, 23, 42, 0.05)",
      }}
    >
      <Box>
        {/* Header Title & Status Badge */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                letterSpacing: "-0.01em",
              }}
            >
              Attendance Quick View
            </Typography>
            <Chip
              size="small"
              label={`${inOfficeCount} In Office`}
              sx={{
                fontWeight: 700,
                fontSize: "0.72rem",
                backgroundColor: isDark ? alpha("#10b981", 0.15) : "#d1fae5",
                color: "#059669",
                borderRadius: "6px",
              }}
            />
          </Stack>
        </Box>

        {/* Attendance Statistics Cards */}
        <Grid container spacing={2}>
          {/* Stat 1: Number of Present Today */}
          <Grid item xs={12} sm={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 1,
                background: isDark ? alpha("#1e293b", 0.5) : "#f8fafc",
                border: 1, borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Number of Present
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#10b981", mt: 0.5 }}>
                {presentTodayCount}{" "}
                <Typography component="span" variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                  / {totalStaff} Staff
                </Typography>
              </Typography>
            </Paper>
          </Grid>

          {/* Stat 2: Present Percentage */}
          <Grid item xs={12} sm={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 1,
                background: isDark ? alpha("#1e293b", 0.5) : "#f8fafc",
                border: 1, borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Present Percentage
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "primary.main", mt: 0.5 }}>
                {presentPercentageToday}%
              </Typography>
            </Paper>
          </Grid>

          {/* Stat 3: 10-Day Overall Attendance Percentage */}
          <Grid item xs={12} sm={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 1,
                background: isDark ? alpha("#1e293b", 0.5) : "#f8fafc",
                border: 1, borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Overall Attendance (10 Days)
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "primary.main", mt: 0.5 }}>
                {tenDayAttendanceAvg}%
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default AttendanceQuickView;
